import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, Sparkles, ArrowRightCircle, History, Trash2, User, Globe } from "lucide-react";

const STATUS_PILL = {
  new: "bg-sky-50 text-sky-700 border-sky-200",
  qualified: "bg-amber-50 text-amber-800 border-amber-200",
  unqualified: "bg-slate-100 text-slate-600 border-slate-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
  archived: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

const SOURCE_LABEL = {
  contact_form: "Contact Form",
  custom_design: "Custom Design",
  referral: "Referral",
  direct: "Direct",
  import: "Import",
  admin_manual: "Manual",
  other: "Other",
};

const AdminLeadDetail = () => {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [convertForm, setConvertForm] = useState({ display_name: "", client_type: "individual", notes: "" });
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.get(`/admin/leads/${leadId}`);
        setLead(data);
        setConvertForm((f) => ({ ...f, display_name: data.display_name || "", notes: data.notes || "" }));
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        if (err?.response?.status === 404) {
          toast.error("Lead not found");
          setLoading(false);
          navigate("/admin/leads");
          return;
        }
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    setLoading(false);
    toast.error("Could not load lead", { description: lastErr?.response?.data?.detail || "" });
  }, [leadId, navigate]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/leads/${leadId}/history`);
      setHistory(data);
    } catch (e) { /* silent */ if (typeof console !== "undefined") console.debug("[silent]", e); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const patchField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, patch);
      setLead(data);
      toast.success("Saved", { duration: 1200 });
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const submitConvert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/convert`, {
        display_name: convertForm.display_name.trim() || null,
        client_type: convertForm.client_type,
        notes: convertForm.notes || null,
      });
      toast.success("Lead converted to Client", {
        description: `${data.client_number} · ${data.enquiries_rewired} enquiries preserved`,
      });
      navigate(`/admin/clients/${data.client_id}`);
    } catch (err) {
      toast.error("Conversion failed", { description: err?.response?.data?.detail || "" });
    } finally { setConverting(false); setShowConvert(false); }
  };

  const softDelete = async () => {
    if (!window.confirm("Archive this lead? It will be hidden but not permanently deleted.")) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success("Lead archived");
      navigate("/admin/leads");
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || "" });
    }
  };

  if (loading || !lead) {
    return <AdminLayout><div className="py-10 text-center text-sm text-muted-foreground">Loading lead…</div></AdminLayout>;
  }

  const isConverted = !!lead.converted_to_client_id;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-lead-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin/leads")} className="text-xs uppercase tracking-wider text-muted-foreground hover:underline flex items-center gap-1" data-testid="back-to-leads">
              <ArrowLeft className="h-3 w-3" /> Back to Leads
            </button>
            <h1 className="text-2xl font-semibold tracking-tight mt-2" data-testid="lead-display-name">{lead.display_name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="font-mono" data-testid="lead-no">{lead.lead_no}</span>
              <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider border rounded ${STATUS_PILL[lead.status] || STATUS_PILL.new}`} data-testid="lead-status-pill">{lead.status}</span>
              <span className="inline-flex items-center gap-1 text-xs">
                <Sparkles className="h-3 w-3" /> {SOURCE_LABEL[lead.lead_source] || lead.lead_source}
              </span>
              {isConverted && (
                <Link to={`/admin/clients/${lead.converted_to_client_id}`} className="text-xs text-emerald-700 underline" data-testid="link-to-converted-client">
                  → View Client Record
                </Link>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isConverted && (
              <Button data-testid="convert-lead-btn" onClick={() => setShowConvert(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <ArrowRightCircle className="h-4 w-4 mr-1.5" /> Convert to Client
              </Button>
            )}
            <Button variant="outline" data-testid="archive-lead-btn" onClick={softDelete}>
              <Trash2 className="h-4 w-4 mr-1.5" /> Archive
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Details form */}
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="text-base">Lead details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <fieldset disabled={isConverted} className={isConverted ? "opacity-60" : ""}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Display name</Label>
                    <Input data-testid="lead-name-input" defaultValue={lead.display_name} onBlur={(e) => { if (e.target.value !== lead.display_name) patchField({ display_name: e.target.value }); }} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={lead.status} onValueChange={(v) => patchField({ status: v })}>
                      <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="unqualified">Unqualified</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="lead-email-input" defaultValue={lead.primary_email || ""} onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== lead.primary_email) patchField({ primary_email: v }); }} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input data-testid="lead-phone-input" defaultValue={lead.primary_phone || ""} onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== lead.primary_phone) patchField({ primary_phone: v }); }} />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Select value={lead.lead_source} onValueChange={(v) => patchField({ lead_source: v })}>
                      <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input data-testid="lead-country-input" defaultValue={lead.country || "IN"} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== lead.country) patchField({ country: v }); }} />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Source details</Label>
                  <Input data-testid="lead-source-details-input" defaultValue={lead.source_details || ""} onBlur={(e) => { const v = e.target.value || null; if (v !== lead.source_details) patchField({ source_details: v }); }} />
                </div>
                <div className="mt-3">
                  <Label>Notes</Label>
                  <Textarea data-testid="lead-notes-input" rows={3} defaultValue={lead.notes || ""} onBlur={(e) => { const v = e.target.value || null; if (v !== lead.notes) patchField({ notes: v }); }} />
                </div>
              </fieldset>
              {saving && <div className="text-xs text-muted-foreground">Saving…</div>}
              {isConverted && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                  This Lead has been converted. Manage further details on the Client record.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-sm uppercase tracking-wider">Contact</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                {lead.primary_email && (
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> <a href={`mailto:${lead.primary_email}`} className="underline">{lead.primary_email}</a></div>
                )}
                {lead.primary_phone && (
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {lead.primary_phone}</div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" /> {lead.country || "—"}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <User className="h-3.5 w-3.5" /> Created {lead.created_at?.slice(0, 10)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm uppercase tracking-wider">At a glance</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Enquiries</span> <span className="font-mono">{lead.enquiry_count || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Last enquiry</span> <span className="text-xs">{lead.last_enquiry_at?.slice(0, 10) || "—"}</span></div>
                {lead.converted_at && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Converted</span> <span className="text-xs">{lead.converted_at.slice(0, 10)}</span></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Timeline */}
        <Card data-testid="lead-timeline-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Enquiry timeline</CardTitle></CardHeader>
          <CardContent>
            {!history ? (
              <div className="text-sm text-muted-foreground">Loading timeline…</div>
            ) : history.events.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4" data-testid="lead-timeline-empty">No enquiries attached yet.</div>
            ) : (
              <ol className="relative border-l ml-3 space-y-4" data-testid="lead-timeline-list">
                {history.events.map((ev) => (
                  <li key={ev.id} className="pl-4" data-testid={`timeline-event-${ev.id}`}>
                    <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-sky-500 border-2 border-white" />
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{ev.reference}</span>
                      <span className="text-sm font-medium">{ev.title || "—"}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded uppercase tracking-wider">{ev.type}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{ev.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {ev.message && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{ev.message}</p>}
                    <Link to={`/admin/enquiries?highlight=${ev.id}`} className="text-xs underline text-slate-500">Open enquiry</Link>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Convert dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent data-testid="convert-lead-dialog">
          <DialogHeader><DialogTitle>Convert Lead to Client</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">This will create a new Client record and rewire every enquiry from this Lead to the Client. The complete enquiry history is preserved.</p>
            <div>
              <Label>Client name</Label>
              <Input data-testid="convert-client-name" value={convertForm.display_name} onChange={(e) => setConvertForm({ ...convertForm, display_name: e.target.value })} />
            </div>
            <div>
              <Label>Client type</Label>
              <Select value={convertForm.client_type} onValueChange={(v) => setConvertForm({ ...convertForm, client_type: v })}>
                <SelectTrigger data-testid="convert-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes to carry over</Label>
              <Textarea data-testid="convert-client-notes" rows={2} value={convertForm.notes} onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvert(false)}>Cancel</Button>
            <Button data-testid="submit-convert-lead" onClick={submitConvert} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700">
              {converting ? "Converting…" : "Convert & open Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLeadDetail;
